// ============================================================
// API-BACKED ENGINE COMPATIBILITY LAYER
// ============================================================
// Mirror the in-memory engine API but call REST endpoints.
// Modules don't need to change — these classes maintain local
// state (Maps, arrays) and sync with the API on writes.
// ============================================================

import { api } from './api-client.js';

// ============================================================
// Griffin Config — server-side adapter management
// ============================================================

export const griffinConfig = {
  get: () => api.get('/config/griffin'),
  set: (enabled, apiKey) => api.post('/config/griffin', { enabled, apiKey }),
};

// ============================================================
// ApiLedger
// ============================================================

export class ApiLedger {
  constructor() {
    this.accounts = new Map();
    this.transactions = new Map();
    this.entries = [];
    this.accruals = new Map();
    this._balances = new Map();
    this.eventBus = new ApiEventBus();
  }

  async load() {
    const [accounts, transactions, entries, accruals] = await Promise.all([
      api.get('/ledger/accounts'),
      api.get('/ledger/transactions'),
      api.get('/ledger/entries'),
      api.get('/ledger/accruals'),
    ]);

    this.accounts = new Map();
    this._balances = new Map();
    for (const acc of accounts) {
      const { balance, ...rest } = acc;
      // Add helper methods that modules expect
      rest.canTransition = function(newStatus) {
        const VALID = { PENDING: ['ACTIVE','CLOSED'], ACTIVE: ['DORMANT','FROZEN','CLOSING'], DORMANT: ['ACTIVE','FROZEN','CLOSING'], FROZEN: ['ACTIVE','CLOSING'], CLOSING: ['CLOSED'], CLOSED: [] };
        return VALID[this.status]?.includes(newStatus) || false;
      };
      rest.isOperational = function() { return this.status === 'ACTIVE'; };
      this.accounts.set(acc.id, rest);
      if (balance) this._balances.set(acc.id, balance);
    }

    this.transactions = new Map();
    for (const txn of transactions) {
      this.transactions.set(txn.id, txn);
    }

    this.entries = entries || [];

    this.accruals = new Map();
    if (accruals) {
      for (const [id, acc] of Object.entries(accruals)) {
        this.accruals.set(id, acc);
      }
    }
  }

  // ---- Sync reads ----

  getAllAccounts(filter = {}) {
    let result = Array.from(this.accounts.values());
    if (filter.type) result = result.filter(a => a.type === filter.type);
    if (filter.currency) result = result.filter(a => a.currency === filter.currency);
    if (filter.status) result = result.filter(a => a.status === filter.status);
    if (filter.customerId) result = result.filter(a => a.customerId === filter.customerId);
    if (filter.internal === true) result = result.filter(a => a.metadata?.internal === true);
    if (filter.internal === false) result = result.filter(a => !a.metadata?.internal);
    return result;
  }

  getAccount(id) {
    return this.accounts.get(id) || null;
  }

  getBalance(id) {
    return this._balances.get(id) || { posted: 0, pending: 0, available: 0, version: 0 };
  }

  getTransactionsForAccount(accountId) {
    const txns = [];
    for (const txn of this.transactions.values()) {
      if (txn.entries?.some(e => e.accountId === accountId)) txns.push(txn);
    }
    return txns;
  }

  getTrialBalance(currency) {
    const result = [];
    for (const [id, acc] of this.accounts) {
      if (currency && acc.currency !== currency) continue;
      const bal = this.getBalance(id);
      const isDebitNormal = acc.type === 'ASSET' || acc.type === 'EXPENSE';
      result.push({
        accountId: id,
        accountNumber: acc.accountNumber,
        name: acc.name,
        type: acc.type,
        currency: acc.currency,
        debitBalance: isDebitNormal ? Math.max(0, bal.posted) : Math.max(0, -bal.posted),
        creditBalance: isDebitNormal ? Math.max(0, -bal.posted) : Math.max(0, bal.posted),
      });
    }
    return result;
  }

  verifyIntegrity() {
    const stats = {
      accounts: this.accounts.size,
      entries: this.entries.length,
      transactions: this.transactions.size,
    };
    const issues = [];

    // Check each transaction balances
    for (const txn of this.transactions.values()) {
      if (txn.entries && txn.entries.length > 0) {
        const debits = txn.entries.filter(e => e.direction === 'DEBIT').reduce((s, e) => s + e.amount, 0);
        const credits = txn.entries.filter(e => e.direction === 'CREDIT').reduce((s, e) => s + e.amount, 0);
        if (Math.abs(debits - credits) > 0.001) {
          issues.push(`Transaction ${txn.id} imbalanced: DR ${debits} != CR ${credits}`);
        }
      }
    }

    return { valid: issues.length === 0, stats, issues };
  }

  reconstructBalance(accountId) {
    const acc = this.accounts.get(accountId);
    if (!acc) return 0;
    const isDebitNormal = acc.type === 'ASSET' || acc.type === 'EXPENSE';
    let balance = 0;
    for (const entry of this.entries) {
      if (entry.accountId !== accountId) continue;
      if (entry.direction === 'DEBIT') {
        balance += isDebitNormal ? entry.amount : -entry.amount;
      } else {
        balance += isDebitNormal ? -entry.amount : entry.amount;
      }
    }
    return Math.round(balance * 100) / 100;
  }

  // ---- Async writes ----

  async createAccount(body) {
    const result = await api.post('/ledger/accounts', body);
    const { balance, ...acc } = result;
    acc.canTransition = function(s) { const V = { PENDING:['ACTIVE','CLOSED'], ACTIVE:['DORMANT','FROZEN','CLOSING'], DORMANT:['ACTIVE','FROZEN','CLOSING'], FROZEN:['ACTIVE','CLOSING'], CLOSING:['CLOSED'], CLOSED:[] }; return V[this.status]?.includes(s)||false; };
    acc.isOperational = function() { return this.status === 'ACTIVE'; };
    this.accounts.set(acc.id, acc);
    if (balance) this._balances.set(acc.id, balance);
    return acc;
  }

  async postTransaction(body) {
    const txn = await api.post('/ledger/transactions', body);
    this.transactions.set(txn.id, txn);
    return txn;
  }

  async transitionAccountStatus(id, newStatus, changedBy, reason) {
    const result = await api.post(`/ledger/accounts/${id}/transition`, { newStatus, changedBy, reason });
    return result;
  }

  async placeHold(body) {
    return api.post('/ledger/holds', body);
  }

  async releaseHold(id) {
    return api.post(`/ledger/holds/${id}/release`);
  }

  async reverseTransaction(id, reason, reversedBy) {
    return api.post(`/ledger/transactions/${id}/reverse`, { reason, reversedBy });
  }
}


// ============================================================
// ApiPaymentOrchestrator
// ============================================================

export class ApiPaymentOrchestrator {
  constructor() {
    this._payments = [];
  }

  async load() {
    this._payments = await api.get('/payments');
  }

  // ---- Sync reads ----

  getAllPayments() { return this._payments; }

  getPayment(id) { return this._payments.find(p => p.id === id) || null; }

  getPendingApprovals() {
    return this._payments.filter(p =>
      p.status === 'PENDING_APPROVAL' ||
      (p.status === 'INITIATED' && p.metadata?.via === 'customer-portal')
    );
  }

  getDailyLimitSummary() {
    // Fetch sync from cache — call API for full data on load
    return this._dailyLimits || [];
  }

  getNostroSummary() {
    return this._nostroSummary || {};
  }

  getPaymentsByAccount(accountId) {
    return this._payments.filter(p => p.sourceAccountId === accountId || p.targetAccountId === accountId);
  }

  // ---- Async writes ----

  async initiatePayment(body) {
    const pmt = await api.post('/payments', body);
    this._payments.push(pmt);
    return pmt;
  }

  async validatePayment(id) {
    const result = await api.post(`/payments/${id}/validate`);
    this._updatePayment(id, result);
    return result;
  }

  async submitPayment(id, opts) {
    const result = await api.post(`/payments/${id}/submit`, opts || {});
    this._updatePayment(id, result);
    return result;
  }

  async approvePayment(id, approvedBy) {
    const result = await api.post(`/payments/${id}/approve`, { approvedBy });
    this._updatePayment(id, result);
    return result;
  }

  async rejectApproval(id, rejectedBy, reason) {
    const result = await api.post(`/payments/${id}/reject`, { rejectedBy, reason });
    this._updatePayment(id, result);
    return result;
  }

  async referPayment(id, team, reason) {
    const result = await api.post(`/payments/${id}/refer`, { team, reason });
    this._updatePayment(id, result);
    return result;
  }

  async clearReferral(id, resolution, notes) {
    const result = await api.post(`/payments/${id}/clear-referral`, { resolution, notes });
    this._updatePayment(id, result);
    return result;
  }

  getReferredPayments() {
    return this._payments.filter(p => p.status === 'REFERRED');
  }

  async receiveInboundPayment(body) {
    const pmt = await api.post('/payments/inbound', body);
    this._payments.push(pmt);
    return pmt;
  }

  async returnPayment(id, body) {
    const result = await api.post(`/payments/${id}/return`, body);
    this._updatePayment(id, result);
    return result;
  }

  // Griffin adapter — no-op in API mode (server handles adapters)
  setAdapter() {}

  async loadExtras() {
    [this._dailyLimits, this._nostroSummary] = await Promise.all([
      api.get('/payments/daily-limits'),
      api.get('/payments/nostro'),
    ]);
  }

  _updatePayment(id, data) {
    const idx = this._payments.findIndex(p => p.id === id);
    if (idx >= 0) this._payments[idx] = data;
  }
}


// ============================================================
// ApiBeneficiaryRegistry
// ============================================================

export class ApiBeneficiaryRegistry {
  constructor() {
    this._list = [];
  }

  async load() {
    this._list = await api.get('/beneficiaries');
  }

  getAll() { return this._list; }

  get(id) { return this._list.find(b => b.id === id) || null; }

  getByCustomer(customerId) { return this._list.filter(b => b.customerId === customerId); }

  async add(body) {
    const b = await api.post('/beneficiaries', body);
    this._list.push(b);
    return b;
  }

  async recordUsage(id) {
    await api.post(`/beneficiaries/${id}/usage`);
  }

  async deactivate(id) {
    await api.post(`/beneficiaries/${id}/deactivate`);
    const idx = this._list.findIndex(b => b.id === id);
    if (idx >= 0) this._list[idx].status = 'INACTIVE';
  }
}


// ============================================================
// ApiStandingOrderEngine
// ============================================================

export class ApiStandingOrderEngine {
  constructor() {
    this._orders = [];
  }

  async load() {
    this._orders = await api.get('/standing-orders');
  }

  getAll() { return this._orders; }

  async create(body) {
    const order = await api.post('/standing-orders', body);
    this._orders.push(order);
    return order;
  }

  async execute(id) {
    return api.post(`/standing-orders/${id}/execute`);
  }

  async pause(id) {
    const result = await api.post(`/standing-orders/${id}/pause`);
    this._updateOrder(id, result);
    return result;
  }

  async resume(id) {
    const result = await api.post(`/standing-orders/${id}/resume`);
    this._updateOrder(id, result);
    return result;
  }

  async cancel(id, reason) {
    const result = await api.post(`/standing-orders/${id}/cancel`, { reason });
    this._updateOrder(id, result);
    return result;
  }

  _updateOrder(id, data) {
    const idx = this._orders.findIndex(o => o.id === id);
    if (idx >= 0) this._orders[idx] = data;
  }
}


// ============================================================
// ApiKYC
// ============================================================

export class ApiKYC {
  constructor() {
    this._customers = [];
    this._cases = [];
  }

  async load() {
    [this._customers, this._cases] = await Promise.all([
      api.get('/customers'),
      api.get('/kyc/cases'),
    ]);
    // Ensure individuals is a Map for modules that iterate it
    const custMap = new Map();
    for (const c of this._customers) {
      if (c.individuals && !(c.individuals instanceof Map)) {
        const map = new Map();
        if (Array.isArray(c.individuals)) {
          for (const ind of c.individuals) map.set(ind.id, ind);
        } else if (typeof c.individuals === 'object') {
          for (const [k, v] of Object.entries(c.individuals)) map.set(k, v);
        }
        c.individuals = map;
      }
      custMap.set(c.id, c);
    }
    // Attach customer reference to each case (KYCModule expects case.customer)
    for (const kycCase of this._cases) {
      kycCase.customer = custMap.get(kycCase.customerId) || null;
    }
  }

  // ---- Sync reads ----

  getAllCustomers() { return this._customers; }

  getCustomer(id) { return this._customers.find(c => c.id === id) || null; }

  getAllCases() { return this._cases; }

  getKYCCase(customerId) { return this._cases.find(c => c.customerId === customerId) || null; }

  checkDataCompleteness(customerId) {
    if (this._completeness?.[customerId]) return this._completeness[customerId];
    // Lazy load — trigger async fetch, return placeholder for now
    if (!this._completenessLoading) this._completenessLoading = {};
    if (!this._completenessLoading[customerId]) {
      this._completenessLoading[customerId] = true;
      api.get(`/customers/${customerId}/completeness`)
        .then(data => { if (!this._completeness) this._completeness = {}; this._completeness[customerId] = data; })
        .catch(() => { if (!this._completeness) this._completeness = {}; this._completeness[customerId] = { complete: false, issues: ['Failed to load'] }; });
    }
    return { complete: false, issues: ['Loading...'] };
  }

  // ---- Async writes ----

  async createCustomer(body) {
    const result = await api.post('/customers', body);
    // result is { customer } from KYC engine
    const customer = result.customer || result;
    if (customer.individuals && !(customer.individuals instanceof Map)) {
      const map = new Map();
      if (Array.isArray(customer.individuals)) {
        for (const ind of customer.individuals) map.set(ind.id, ind);
      }
      customer.individuals = map;
    }
    this._customers.push(customer);
    // Also fetch the new KYC case
    try {
      const kycCase = await api.get(`/kyc/cases/${customer.id}`);
      this._cases.push(kycCase);
    } catch (_) { /* ignore */ }
    return result;
  }

  async addIndividual(customerId, body) {
    const result = await api.post(`/customers/${customerId}/individuals`, body);
    return result;
  }

  async updateIndividual(customerId, individualId, body) {
    const result = await api.put(`/customers/${customerId}/individuals/${individualId}`, body);
    return result;
  }

  async submitForScreening(customerId, rmId) {
    const result = await api.post(`/customers/${customerId}/submit-screening`, { by: rmId });
    return result;
  }

  async runScreening(customerId, config) {
    const result = await api.post(`/customers/${customerId}/screening`, { screeningConfig: config });
    return result;
  }

  async submitForRMReview(customerId, rmId) {
    const result = await api.post(`/customers/${customerId}/submit-review`, { by: rmId });
    return result;
  }

  async returnToDataCollection(customerId, by, reason) {
    const result = await api.post(`/customers/${customerId}/return-to-data-collection`, { by, reason });
    return result;
  }

  async returnToScreening(customerId, by, reason) {
    const result = await api.post(`/customers/${customerId}/return-to-screening`, { by, reason });
    return result;
  }

  async triggerReview(customerId, rmId) {
    const result = await api.post(`/customers/${customerId}/trigger-review`, { by: rmId });
    return result;
  }

  async startReview(customerId, rmId) {
    const result = await api.post(`/customers/${customerId}/start-review`, { by: rmId });
    return result;
  }

  async recordRMAssessment(customerId, form) {
    const result = await api.post(`/customers/${customerId}/assessment`, form);
    return result;
  }

  async confirmNoUBOs(customerId, confirmed) {
    const result = await api.post(`/customers/${customerId}/confirm-no-ubos`, { confirmed });
    return result;
  }

  async verifyCH(customerId) {
    const result = await api.post(`/customers/${customerId}/verify-ch`, {});
    return result;
  }

  async getCHSnapshots(customerId) {
    return api.get(`/customers/${customerId}/ch-snapshots`);
  }

  async loadCompleteness() {
    this._completeness = {};
    for (const c of this._customers) {
      try {
        this._completeness[c.id] = await api.get(`/customers/${c.id}/completeness`);
      } catch (err) {
        const msg = err.status === 429
          ? 'Rate limited — please wait a moment and retry'
          : 'Error loading';
        this._completeness[c.id] = { complete: false, issues: [msg] };
      }
    }
  }
}


// ============================================================
// ApiLending
// ============================================================

export class ApiLending {
  constructor() {
    this._facilities = [];
    this.accruals = new Map();
    this.drawdowns = [];
    this.repayments = [];
  }

  async load() {
    const [facilities, accruals, drawdowns, repayments] = await Promise.all([
      api.get('/facilities'),
      api.get('/facilities/data/accruals'),
      api.get('/facilities/data/drawdowns'),
      api.get('/facilities/data/repayments'),
    ]);
    this._facilities = facilities || [];
    this.accruals = new Map();
    if (accruals) {
      for (const [id, acc] of Object.entries(accruals)) this.accruals.set(id, acc);
    }
    this.drawdowns = drawdowns || [];
    this.repayments = repayments || [];
  }

  // ---- Sync reads ----

  getAllFacilities() { return this._facilities; }

  getFacility(id) { return this._facilities.find(f => f.id === id) || null; }

  getSchedule(id) {
    if (this._schedules?.[id]) return this._schedules[id];
    // Lazy load
    if (!this._scheduleLoading) this._scheduleLoading = {};
    if (!this._scheduleLoading[id]) {
      this._scheduleLoading[id] = true;
      api.get(`/facilities/${id}/schedule`)
        .then(data => { if (!this._schedules) this._schedules = {}; this._schedules[id] = data; })
        .catch(() => { if (!this._schedules) this._schedules = {}; this._schedules[id] = []; });
    }
    return [];
  }

  // ---- Async writes ----

  async createFacility(body) {
    const fac = await api.post('/facilities', body);
    this._facilities.push(fac);
    return fac;
  }

  async approveFacility(id, approvedBy, conditions) {
    const result = await api.post(`/facilities/${id}/approve`, { approvedBy, conditions });
    this._updateFacility(id, result);
    return result;
  }

  async activateFacility(id, by) {
    const result = await api.post(`/facilities/${id}/activate`, { by });
    this._updateFacility(id, result);
    return result;
  }

  async drawdown(id, amount, targetAccountId) {
    const result = await api.post(`/facilities/${id}/drawdown`, { amount, targetAccountId });
    return result;
  }

  async repay(id, amount, sourceAccountId) {
    const result = await api.post(`/facilities/${id}/repay`, { amount, sourceAccountId });
    return result;
  }

  async accrueInterest(id, date) {
    const result = await api.post(`/facilities/${id}/accrue-interest`, { date: date?.toISOString() });
    return result;
  }

  async chargeAccruedInterest(id, targetAccountId) {
    const result = await api.post(`/facilities/${id}/charge-interest`, { targetAccountId });
    return result;
  }

  async migrateStage(id, newStage, reason) {
    const result = await api.post(`/facilities/${id}/migrate-stage`, { newStage, reason });
    this._updateFacility(id, result);
    return result;
  }

  async calculateECL(id, params) {
    const result = await api.post(`/facilities/${id}/ecl`, params);
    return result;
  }

  async checkCovenants(id, data) {
    const result = await api.post(`/facilities/${id}/covenants`, data);
    return result;
  }

  async loadSchedules() {
    this._schedules = {};
    for (const f of this._facilities) {
      try {
        this._schedules[f.id] = await api.get(`/facilities/${f.id}/schedule`);
      } catch (_) {
        this._schedules[f.id] = [];
      }
    }
  }

  _updateFacility(id, data) {
    const idx = this._facilities.findIndex(f => f.id === id);
    if (idx >= 0) this._facilities[idx] = data;
  }
}


// ============================================================
// ApiMonitoring
// ============================================================

export class ApiMonitoring {
  constructor() {
    this.alerts = [];
    this._profiles = {};
    this._summaries = {};
  }

  async load() {
    this.alerts = await api.get('/monitoring/alerts/all');
  }

  // ---- Sync reads ----

  getAllOpenAlerts() {
    return this.alerts.filter(a => a.status === 'OPEN');
  }

  getAlerts(customerId, status) {
    let result = this.alerts.filter(a => a.customerId === customerId);
    if (status) result = result.filter(a => a.status === status);
    return result;
  }

  getMonitoringSummary(customerId) {
    if (this._summaries[customerId]) return this._summaries[customerId];
    // Lazy load
    if (!this._summaryLoading) this._summaryLoading = {};
    if (!this._summaryLoading[customerId]) {
      this._summaryLoading[customerId] = true;
      api.get(`/monitoring/summary/${customerId}`)
        .then(data => { this._summaries[customerId] = data; })
        .catch(() => {});
    }
    return { totalTransactions: 0, alertCounts: { open: 0, escalated: 0, total: 0 }, profile: null };
  }

  getProfile(customerId) {
    if (this._profiles[customerId]) return this._profiles[customerId];
    // Lazy load
    if (!this._profileLoading) this._profileLoading = {};
    if (!this._profileLoading[customerId]) {
      this._profileLoading[customerId] = true;
      api.get(`/monitoring/profiles/${customerId}`)
        .then(data => { this._profiles[customerId] = data; })
        .catch(() => {});
    }
    return null;
  }

  // ---- Async writes ----

  async reviewAlert(alertId, body) {
    const result = await api.post(`/monitoring/alerts/${alertId}/review`, body);
    return result;
  }

  async registerProfile(customerId, profile, riskRating) {
    const result = await api.post('/monitoring/profiles', { customerId, profile, riskRating });
    return result;
  }

  async loadProfiles(customerIds) {
    for (const id of customerIds) {
      try {
        this._summaries[id] = await api.get(`/monitoring/summary/${id}`);
        this._profiles[id] = await api.get(`/monitoring/profiles/${id}`);
      } catch (_) { /* no profile yet */ }
    }
  }
}


// ============================================================
// ApiCompliance
// ============================================================

export class ApiCompliance {
  constructor() {
    this._sars = [];
    this._dashboard = null;
  }

  async load() {
    try {
      [this._sars, this._dashboard] = await Promise.all([
        api.get('/sars'),
        api.get('/compliance/dashboard'),
      ]);
    } catch (e) {
      // 403 expected for users without compliance role
      if (e.status === 403) {
        this._sars = [];
        this._dashboard = null;
      } else {
        throw e;
      }
    }
  }

  // ---- Sync reads ----

  getAllSARs() { return this._sars; }

  getSAR(id) { return this._sars.find(s => s.id === id) || null; }

  getComplianceDashboard() {
    return this._dashboard || { draft: 0, underReview: 0, submitted: 0, acknowledged: 0, overdue: 0, byRisk: {}, recentSARs: [] };
  }

  // ---- Async writes ----

  async createSAR(body) {
    const sar = await api.post('/sars', body);
    this._sars.push(sar);
    return sar;
  }

  async updateSAR(id, body) {
    const result = await api.put(`/sars/${id}`, body);
    this._updateSAR(id, result);
    return result;
  }

  async reviewSAR(id, reviewedBy) {
    const result = await api.post(`/sars/${id}/review`, { reviewedBy });
    this._updateSAR(id, result);
    return result;
  }

  async submitSAR(id, submittedBy) {
    const result = await api.post(`/sars/${id}/submit`, { submittedBy });
    this._updateSAR(id, result);
    return result;
  }

  async acknowledgeSAR(id, ncaReference) {
    const result = await api.post(`/sars/${id}/acknowledge`, { ncaReference });
    this._updateSAR(id, result);
    return result;
  }

  async generateCTR(customerId, dateFrom, dateTo) {
    return api.post('/compliance/reports/ctr', {
      customerId,
      dateFrom: dateFrom?.toISOString?.() || dateFrom,
      dateTo: dateTo?.toISOString?.() || dateTo,
    });
  }

  async getHighRiskCustomerReport() {
    return api.get('/compliance/reports/high-risk');
  }

  async getAlertResolutionReport(dateFrom, dateTo) {
    const params = new URLSearchParams();
    if (dateFrom) params.set('from', dateFrom.toISOString());
    if (dateTo) params.set('to', dateTo.toISOString());
    return api.get(`/compliance/reports/alert-resolution?${params}`);
  }

  _updateSAR(id, data) {
    const idx = this._sars.findIndex(s => s.id === id);
    if (idx >= 0) this._sars[idx] = data;
  }
}


// ============================================================
// ApiCRM
// ============================================================

export class ApiCRM {
  constructor() {
    this.contacts = new Map();
    this.tasks = new Map();
    this.complaints = new Map();
  }

  async load() {
    const [contacts, tasks, complaints] = await Promise.all([
      api.get('/crm/contacts'),
      api.get('/crm/tasks'),
      api.get('/crm/complaints'),
    ]);
    this.contacts = new Map();
    for (const c of contacts) this.contacts.set(c.id, c);
    this.tasks = new Map();
    for (const t of tasks) this.tasks.set(t.id, t);
    this.complaints = new Map();
    for (const comp of complaints) this.complaints.set(comp.id, comp);
  }

  // ---- Sync reads ----

  getContactHistory(customerId) {
    return Array.from(this.contacts.values())
      .filter(c => c.customerId === customerId)
      .sort((a, b) => new Date(b.date) - new Date(a.date));
  }

  getTasksByRM(rmId) {
    return Array.from(this.tasks.values())
      .filter(t => t.assignedTo === rmId && t.status === 'OPEN');
  }

  getTasksByCustomer(customerId) {
    return Array.from(this.tasks.values()).filter(t => t.customerId === customerId);
  }

  getOverdueTasks() {
    const now = new Date().toISOString();
    return Array.from(this.tasks.values())
      .filter(t => t.status === 'OPEN' && t.dueDate && t.dueDate < now);
  }

  getRMPortfolio(rmId) { return this._portfolio || []; }
  getCustomer360(customerId) { return this._customer360 || {}; }
  getOnboardingPipeline() { return this._pipelines?.onboarding || {}; }
  getLendingPipeline() { return this._pipelines?.lending || {}; }

  // ---- Async reads (fetch from server) ----

  async loadPortfolio(rmId) {
    this._portfolio = await api.get(`/crm/portfolio/${rmId}`);
    return this._portfolio;
  }

  async loadCustomer360(customerId) {
    this._customer360 = await api.get(`/crm/customer360/${customerId}`);
    return this._customer360;
  }

  async loadCustomer360Enhanced(customerId) {
    this._customer360Enhanced = await api.get(`/crm/customer360/${customerId}/enhanced`);
    return this._customer360Enhanced;
  }

  async loadCustomerInsights(customerId) {
    this._customerInsights = await api.get(`/crm/customer360/${customerId}/insights`);
    return this._customerInsights;
  }

  async loadPipelines() {
    this._pipelines = await api.get('/crm/pipelines');
    return this._pipelines;
  }

  // ---- Async writes ----

  async addContactRecord(customerId, data) {
    const contact = await api.post('/crm/contacts', { customerId, ...data });
    this.contacts.set(contact.id, contact);
    return contact;
  }

  async createTask(data) {
    const task = await api.post('/crm/tasks', data);
    this.tasks.set(task.id, task);
    return task;
  }

  async completeTask(taskId, completedBy, notes) {
    const task = await api.post(`/crm/tasks/${taskId}/complete`, { completedBy, notes });
    this.tasks.set(task.id, task);
    return task;
  }

  async reassignTask(taskId, assignedTo) {
    const task = await api.post(`/crm/tasks/${taskId}/reassign`, { assignedTo });
    this.tasks.set(task.id, task);
    return task;
  }

  // ---- Complaint sync reads ----

  getComplaintsByCustomer(customerId) {
    return Array.from(this.complaints.values())
      .filter(c => c.customerId === customerId)
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }

  getOpenComplaints() {
    return Array.from(this.complaints.values())
      .filter(c => c.status !== 'RESOLVED' && c.status !== 'ESCALATED_TO_FOS');
  }

  getOverdueComplaints() {
    const now = Date.now();
    const EIGHT_WEEKS = 8 * 7 * 24 * 60 * 60 * 1000;
    return Array.from(this.complaints.values())
      .filter(c => {
        if (c.status === 'RESOLVED' || c.status === 'ESCALATED_TO_FOS') return false;
        return (now - new Date(c.receivedDate).getTime()) >= EIGHT_WEEKS;
      });
  }

  getComplaintsDashboard() { return this._complaintsDashboard || {}; }

  async loadComplaintsDashboard() {
    this._complaintsDashboard = await api.get('/crm/complaints/dashboard');
    return this._complaintsDashboard;
  }

  // ---- Complaint async writes ----

  async createComplaint(customerId, data) {
    const complaint = await api.post('/crm/complaints', { customerId, ...data });
    this.complaints.set(complaint.id, complaint);
    return complaint;
  }

  async acknowledgeComplaint(complaintId, acknowledgedBy) {
    const complaint = await api.post(`/crm/complaints/${complaintId}/acknowledge`, { acknowledgedBy });
    this.complaints.set(complaint.id, complaint);
    return complaint;
  }

  async updateComplaint(complaintId, data) {
    const complaint = await api.put(`/crm/complaints/${complaintId}`, data);
    this.complaints.set(complaint.id, complaint);
    return complaint;
  }

  async resolveComplaint(complaintId, data) {
    const complaint = await api.post(`/crm/complaints/${complaintId}/resolve`, data);
    this.complaints.set(complaint.id, complaint);
    return complaint;
  }

  async escalateToFOS(complaintId, data) {
    const complaint = await api.post(`/crm/complaints/${complaintId}/escalate`, data);
    this.complaints.set(complaint.id, complaint);
    return complaint;
  }

  async reopenComplaint(complaintId, data) {
    const complaint = await api.post(`/crm/complaints/${complaintId}/reopen`, data);
    this.complaints.set(complaint.id, complaint);
    return complaint;
  }
}


// ============================================================
// ApiEOD
// ============================================================

export class ApiEOD {
  constructor() {
    this.eodLog = [];
    this.interestEngine = new ApiInterestEngine();
    this.statementEngine = new ApiStatementEngine();
  }

  async load() {
    try {
      const [log, statements] = await Promise.all([
        api.get('/eod/log'),
        api.get('/eod/statements'),
      ]);
      this.eodLog = log || [];
      this.statementEngine.statements = statements || [];
    } catch (e) {
      // 403 expected for users without operations role
      if (e.status === 403) {
        this.eodLog = [];
        this.statementEngine.statements = [];
      } else {
        throw e;
      }
    }
  }

  async runEOD(date, opts) {
    const result = await api.post('/eod/run', {
      date: date?.toISOString?.() || date,
      monthEnd: opts?.monthEnd,
    });
    this.eodLog.push(result);
    return result;
  }
}

class ApiInterestEngine {
  async runDailyAccrual(date) {
    return api.post('/eod/accrue-interest', {
      date: date?.toISOString?.() || date,
    });
  }
}

class ApiStatementEngine {
  constructor() {
    this.statements = [];
  }

  async generateStatement(accountId, periodFrom, periodTo) {
    const stmt = await api.post('/eod/generate-statement', {
      accountId, periodFrom, periodTo,
    });
    this.statements.push(stmt);
    return stmt;
  }
}


// ============================================================
// ApiEventBus
// ============================================================

export class ApiEventBus {
  constructor() {
    this.eventLog = [];
  }

  async load() {
    this.eventLog = await api.get('/events');
  }
}


// ============================================================
// initApiEngines — initialize all engines from API
// ============================================================

export async function initApiEngines() {
  const ledger = new ApiLedger();
  const orchestrator = new ApiPaymentOrchestrator();
  const beneficiaries = new ApiBeneficiaryRegistry();
  const standingOrders = new ApiStandingOrderEngine();
  const kyc = new ApiKYC();
  const lending = new ApiLending();
  const monitoring = new ApiMonitoring();
  const compliance = new ApiCompliance();
  const crm = new ApiCRM();
  const eod = new ApiEOD();
  const eventBus = ledger.eventBus;

  // Load all caches in parallel
  await Promise.all([
    ledger.load(),
    orchestrator.load(),
    beneficiaries.load(),
    standingOrders.load(),
    kyc.load(),
    lending.load(),
    monitoring.load(),
    compliance.load(),
    crm.load(),
    eod.load(),
    eventBus.load(),
  ]);

  // Load extras (daily limits, nostro — small fixed-count calls only)
  // Per-customer/per-facility calls (profiles, schedules, completeness)
  // are loaded lazily on demand to avoid hitting rate limits
  await orchestrator.loadExtras();

  // Build customerAccounts map
  const customerAccounts = {};
  for (const [accId, acc] of ledger.accounts) {
    if (acc.customerId && !acc.metadata?.internal) {
      customerAccounts[acc.customerId] = accId;
    }
  }

  const engines = {
    ledger, orchestrator, beneficiaries, standingOrders,
    kyc, lending, monitoring, compliance, crm, eod, eventBus,
    customerAccounts,
  };

  // Reload function — refreshes all caches
  engines.reload = async () => {
    await Promise.all([
      ledger.load(),
      orchestrator.load(),
      beneficiaries.load(),
      standingOrders.load(),
      kyc.load(),
      lending.load(),
      monitoring.load(),
      compliance.load(),
      crm.load(),
      eod.load(),
      eventBus.load(),
    ]);
    await orchestrator.loadExtras();
    // Clear lazy caches so they reload on next access
    monitoring._summaries = {};
    monitoring._profiles = {};
    lending._schedules = {};
    kyc._completeness = {};
    // Update customerAccounts
    for (const [accId, acc] of ledger.accounts) {
      if (acc.customerId && !acc.metadata?.internal) {
        engines.customerAccounts[acc.customerId] = accId;
      }
    }
  };

  return engines;
}
